import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/atoms/Badge';
import type { HealthOverviewParameter } from '@/domain/health';

import {
    HEALTH_ALERT_LEVEL_ICON_SIZE,
    HEALTH_ALERT_TREND_ICON_SIZE,
    HEALTH_DETAIL_STABLE_TREND_COLOR,
    renderHealthParameterTrendIcon,
    resolveHealthParameterTrendFromDevelopment,
} from '../../_utils/healthAlertTrendDisplay';
import type { HealthAlertBadgeVariant } from '../../HealthDashboard/healthAlertDisplay';
import {
    getHealthParameterDevelopmentLabel,
    getHealthParameterDevelopmentLevel,
    getHealthParameterDevelopmentTooltip,
} from '../../HealthDashboard/healthParameterMetricDisplay';
import HealthAlarmMetricTooltip from '../HealthAlarmMetricTooltip/HealthAlarmMetricTooltip';

import styles from './HealthParameterDevelopmentMetric.module.scss';

export interface HealthParameterDevelopmentMetricProps {
    parameter: HealthOverviewParameter | undefined;
    variant?: HealthAlertBadgeVariant;
}

/**
 * Development row — neutral styling; direction shown with arrow only.
 */
export default function HealthParameterDevelopmentMetric(
    props: HealthParameterDevelopmentMetricProps
) {
    const { parameter, variant = 'inline' } = props;
    const { t } = useTranslation('healthModule');

    if (parameter == null) {
        return (
            <span className={styles.unavailable} data-testid="health-development-metric-no-data">
                {t('dashboard.notAvailable')}
            </span>
        );
    }

    const development = getHealthParameterDevelopmentLevel(parameter);
    const label = getHealthParameterDevelopmentLabel(development, t);
    const tooltip = getHealthParameterDevelopmentTooltip(development, t);
    const trend = resolveHealthParameterTrendFromDevelopment(development);
    const trendColor = HEALTH_DETAIL_STABLE_TREND_COLOR;
    const trendIconSize =
        variant === 'pill' ? HEALTH_ALERT_LEVEL_ICON_SIZE : HEALTH_ALERT_TREND_ICON_SIZE;

    const trendIcon = renderHealthParameterTrendIcon(trend, trendColor, trendIconSize);
    // The alert card's pill matches the status pill beside it; the colours stay the plain
    // text and light border it always had.
    const badge =
        variant === 'pill' ? (
            <Badge size="md" tone="plain" iconEnd={trendIcon}>
                {label}
            </Badge>
        ) : (
            <span
                className={styles.development}
                data-development={development}
                data-testid={`health-development-metric-${development}`}>
                <span className={styles.label}>{label}</span>
                <span className={styles.trendIcon}>{trendIcon}</span>
            </span>
        );

    if (tooltip == null) {
        return badge;
    }

    return (
        <HealthAlarmMetricTooltip label={label} description={tooltip} variant={variant}>
            {badge}
        </HealthAlarmMetricTooltip>
    );
}
